import { useMemo } from 'react';
import { useQueries } from 'react-query';
import {
  getSupportClientLocation,
  getSupportLocationDatasource,
  getSupportLocationAccess,
  getSupportLocationChildren,
  LocationChildrenResponse,
  DataSourceResponse,
  ClientLocationResponse,
  LocationAccessResponse,
  getSupportLocationFeatureFlags,
} from './api';

export const getLocationSupportQueryKey = (key: string, locationId: string) => [
  'location-support-query',
  key,
  locationId,
];
export const useLocationSupportQueries = (locationId?: string, parentId?: string) => {
  const isEnabled = !!locationId && !parentId;

  const [clientLocationQuery, datasourceQuery, accessQuery, childrenQuery, flagsQuery] = useQueries([
    {
      queryKey: getLocationSupportQueryKey('client-location', locationId || ''),
      queryFn: getSupportClientLocation,
      enabled: isEnabled,
    },
    {
      queryKey: getLocationSupportQueryKey('data-source', locationId || ''),
      queryFn: getSupportLocationDatasource,
      enabled: isEnabled,
    },
    {
      queryKey: getLocationSupportQueryKey('access', locationId || ''),
      queryFn: getSupportLocationAccess,
      enabled: isEnabled,
    },
    {
      queryKey: getLocationSupportQueryKey('location', locationId || ''),
      queryFn: () => getSupportLocationChildren(locationId || ''),
      enabled: isEnabled,
    },
    {
      queryKey: getLocationSupportQueryKey('feature-flags', locationId || ''),
      queryFn: () => getSupportLocationFeatureFlags(locationId || ''),
      enabled: isEnabled,
    },
  ]);

  const isLoadingAll = [clientLocationQuery, datasourceQuery, accessQuery, childrenQuery, flagsQuery].some(
    (q) => q.isLoading
  );

  const clientLocation = useMemo(() => clientLocationQuery.data || [], [clientLocationQuery.data]);
  const datasource = useMemo(() => datasourceQuery.data || { SyncApps: [] }, [datasourceQuery.data]);
  const access = useMemo(() => accessQuery.data || [], [accessQuery.data]);
  const children = useMemo(() => childrenQuery.data || [], [childrenQuery.data]);
  const featureFlags = useMemo(() => flagsQuery.data || [], [flagsQuery.data]);

  const mappedCount = useMemo(() => {
    const count = getMappedCount(children, datasource, clientLocation, access);
    return count;
  }, [isLoadingAll, locationId]);

  const hasDepartmentsEnabled = useMemo(() => {
    return featureFlags.some((flag) => flag.Name === 'departments' && flag.Value);
  }, [featureFlags]);

  const hasSmartphonesEnabled = useMemo(() => {
    return featureFlags.some((flag) => flag.Name === 'toggle-user-softphones' && flag.Value);
  }, [featureFlags]);

  return {
    // Raw query values, for the deep diver
    queries: {
      clientLocationQuery,
      datasourceQuery,
      accessQuery,
      childrenQuery,
      flagsQuery,
    },

    // queries data
    clientLocation,
    datasource,
    access,
    children,
    featureFlags,

    // computed values
    mappedCount,
    hasDepartmentsEnabled,
    hasSmartphonesEnabled,
    isLoadingAll,
  };
};

const getMappedCount = (
  children: LocationChildrenResponse,
  dataSource: DataSourceResponse,
  clientLocations: ClientLocationResponse,
  dataAccess: LocationAccessResponse
) => {
  let runningCount = 0;
  for (const location of children) {
    let foundSyncAppForLocation = false;

    if (dataSource.SyncApps) {
      for (const syncApp of dataSource.SyncApps) {
        if (clientLocations) {
          for (const clientLocation of clientLocations) {
            if (clientLocation.SourceID === syncApp.SourceID) {
              for (const accessObj of dataAccess) {
                if (
                  accessObj.ClientLocationID === clientLocation.ClientLocationID &&
                  syncApp.SourceID === accessObj.SourceID &&
                  location.LocationID === accessObj.LocationID
                ) {
                  if (!foundSyncAppForLocation) {
                    foundSyncAppForLocation = true;
                    runningCount += 1;
                  }
                }
              }
            }
          }
        }

        for (const accessObj of dataAccess) {
          if (
            accessObj.ClientLocationID === null &&
            syncApp.SourceID === accessObj.SourceID &&
            location.LocationID === accessObj.LocationID
          ) {
            if (!foundSyncAppForLocation) {
              foundSyncAppForLocation = true;
              runningCount += 1;
            }
          }
        }
      }
    }
  }
  return runningCount;
};
